import React from 'react';
import classNames from 'classnames';

import Data, { _F } from 'data';

import { getIconPath } from 'common';
import Store from 'store';
import EditingContext from 'store/editingContext';
import Tooltip, { RawTooltip } from 'ui/tooltip';
import { ItemIcon, SkillGroupTooltip, SkillTooltip, WithItemTooltip } from 'tooltip';

import BlockClearButton, { builderHasGems, builderSelectSkill } from './common';
import Popup from 'ui/popup';

// [{ id1, id2, gem1, gem2 }]

function sameArray(a, b) {
  if (a.length !== b.length) return false;
  const as = a.slice().sort((x, y) => x - y);
  const bs = b.slice().sort((x, y) => x - y);
  return as.every((value, index) => value === bs[index]);
}
const countGems = builder => builder.gems?.reduce((sum, gem) => sum + (gem.gem1 ? 1 : 0) + (gem.gem2 ? 1 : 0), 0) || 0;

const sortSkillsById = skills => skills.sort((a, b) => {
  if (a.id1 !== b.id1) return a.id1 - b.id1;
  return a.id2 - b.id2;
});

const sortSkills = skills => skills.sort((a, b) => {
  const sa = Data.skills[a.id1 || a.id2], sb = Data.skills[b.id1 || b.id2];
  const ga = Data.skillGroup[a.id1 || a.id2], gb = Data.skillGroup[b.id1 || b.id2];
  if (sa && sb) {
    if (sa.sort !== sb.sort) return sa.sort - sb.sort;
    return sa.learn - sb.learn;    
  } else if (ga && gb) {
    return (ga.name || '').localeCompare(gb.name || '');
  } else {
    return sa ? -1 : 1;
  }
});

function listSkills(charClass) {
  const ids1 = new Set(), ids2 = new Set();
  for (const item of Object.values(Data.items)) {
    if (!item.gem || item.bindType) continue;
    const groups = Data.itemRandom[`${item.gem.random}#${item.grade}`];
    if (!groups) continue;
    for (const group of groups) {
      for (const sub of group.subgroups) {
        for (let i = 0; i < sub.options.length; i += 6) {
          const [type, id, , , , filterClass] = sub.options.slice(i, i + 6);
          if (Data.classMap[filterClass] !== charClass) continue;
          if (type === 5 || type === 34) ids1.add(id);
          if (type === 27 || type === 35) ids2.add(id);
        }
      }
    }
  }
  const result = [...ids1].map(id => ({id1: id}));
  for (const id of ids2) {
    const dst = result.find(({id1}) => {
      if (id1 === id) return true;
      const ga = Data.skillGroup[id1], gb = Data.skillGroup[id];
      if (ga && gb && sameArray(ga.skills, gb.skills)) return true;
      return false;
    });
    if (dst) {
      dst.id2 = id;
    } else {
      result.push({id2: id});
    }
  }
  return sortSkills(result);
}

function gemAcceptsSkill(gem, id, types) {
  const item = Data.items[gem];
  if (!item || !item.gem) return false;
  const groups = Data.itemRandom[`${item.gem.random}#${item.grade}`];
  if (!groups) return false;
  for (const group of groups) {
    for (const sub of group.subgroups) {
      for (let i = 0; i < sub.options.length; i += 6) {
        if (types && !types.includes(sub.options[i])) continue;
        if (sub.options[i + 1] === id) return true;
      }
    }
  }
  return false;
}

function BuilderSkillTooltip({ id }) {
  const skill = Store.useBuilder(builder => Data.skills[id] && builderSelectSkill(builder, id));
  const pvp = Store.useBuilder(builder => builder.category === 'arena' || builder.category === 'gvg');
  if (Data.skillGroup[id]) {
    return <SkillGroupTooltip id={id}/>;
  } else if (skill) {
    return <SkillTooltip skill={skill} pvp={pvp}/>;
  }
}

function GemSkillDisplay({ ids, disabled, onClick }) {
  const data = ids && (Data.skillGroup[ids.id1 || ids.id2] || Data.skills[ids.id1 || ids.id2]);
  if (!data) return null;
  return (
    <RawTooltip tooltip={<BuilderSkillTooltip id={ids.id1 || ids.id2}/>}>
      <div className={classNames("lap-skill-entry", {"lap-disabled": disabled})} onClick={disabled ? null : onClick}>
        <div className="lap-skill-icon" style={{backgroundImage: `url(${getIconPath(data.icon)}`}}/>
        <div className="lap-skill-name">{data.name}</div>
      </div>
    </RawTooltip>
  );
}

function BuilderGemSkillPicker({ charClass, index, onSelect }) {
  const skills = React.useMemo(() => listSkills(charClass), [charClass]);
  const gems = Store.useBuilder(builder => builder.gems);
  return (
    <div className="lap-SkillPicker">
      {skills.map(ids => {
        const disabled = gems?.find((gem, i) => index !== i && (gem.id1 || null) === (ids.id1 || null) && (gem.id2 || null) === (ids.id2 || null));
        return <GemSkillDisplay key={ids.id1 || ids.id2} ids={ids} disabled={disabled} onClick={() => onSelect(ids)}/>
      })}
    </div>
  );
}

const itemBackground = item => item && `lap-item-background lap-item-${Data.itemGrades[item.grade || 0].color}`;

function findInfernoGem(skill, type) {
  for (const [id, item] of Object.entries(Data.items)) {
    if (!item.gem || item.bindType) continue;
    if (gemAcceptsSkill(id, skill, type === 1 ? [5, 34] : [27, 35])) {
      if (item.tier === 3 && item.gem.level === 8) return id;
    }
  }
}

function BuilderGemPicker({ skill, type, onSelect }) {
  const gems = React.useMemo(() => {
    const tiers = {};
    for (const [id, item] of Object.entries(Data.items)) {
      if (!item.gem || item.bindType) continue;
      if (gemAcceptsSkill(id, skill, type === 1 ? [5, 34] : [27, 35])) {
        tiers[item.tier] = (tiers[item.tier] || []);
        tiers[item.tier].push(id);
      }
    }
    return Object.entries(tiers).sort(([a], [b]) => parseInt(b) - parseInt(a)).map(([, list]) => list);
  }, [skill, type]);

  return (
    <div className="lap-GemPicker">
      <div className="lap-gem-socket lap-remove" onClick={() => onSelect(null)}/>
      <div className="lap-tiers">
        {gems.map((row, index) => (
          <div className="lap-tier" key={index}>
            {row.sort((a, b) => Data.items[a].gem?.level - Data.items[b].gem?.level).map(id => {
              const item = Data.items[id];
              return (
                <WithItemTooltip item={{id, skill}}>
                  <div className={classNames("lap-gem-socket", itemBackground(item))} onClick={() => onSelect(id)}>
                    <ItemIcon item={item}/>
                  </div>
                </WithItemTooltip>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

const alignMethod = [{from: "tl", to: "bl"}, {from: "bl", to: "tl"}];

function BuilderGem({ index, editing, charClass }) {
  const [picking, setPicking] = React.useState(null);

  const builder = Store.useBuilder();
  const data = builder.gems?.[index];
  const numGems = countGems(builder);

  const dispatch = Store.useDispatch();
  const onChangeSkill = React.useCallback(ids => dispatch.setBuilder(builder => {
    builder = {...builder};
    builder.gems = builder.gems?.slice() || [];
    const prev = builder.gems[index];
    if (ids) {
      if (!prev) {
        builder.gems.push({...ids});
      } else {
        const dst = builder.gems[index] = {...ids};
        if (prev.gem1 && dst.id1 && gemAcceptsSkill(prev.gem1, dst.id1, [5, 34])) dst.gem1 = prev.gem1;
        if (prev.gem2 && dst.id2 && gemAcceptsSkill(prev.gem2, dst.id2, [27, 35])) dst.gem2 = prev.gem2;
      }
    } else if (prev) {
      builder.gems.splice(index, 1);
    }
    return builder;
  }), [dispatch, index]);
  const onChangeGem1 = React.useCallback(newId => dispatch.setBuilder(builder => {
    if (!builder.gems || !builder.gems[index]) return builder;
    builder = {...builder};
    builder.gems = builder.gems.slice();
    builder.gems[index] = {...builder.gems[index], gem1: newId};
    return builder;
  }), [dispatch, index]);
  const onChangeGem2 = React.useCallback(newId => dispatch.setBuilder(builder => {
    if (!builder.gems || !builder.gems[index]) return builder;
    builder = {...builder};
    builder.gems = builder.gems.slice();
    builder.gems[index] = {...builder.gems[index], gem2: newId};
    return builder;
  }), [dispatch, index]);

  const onSelectSkill = React.useCallback(ids => {
    setPicking(null);
    onChangeSkill(ids);
  }, [onChangeSkill]);
  const onSelectGem1 = React.useCallback(id => {
    setPicking(null);
    onChangeGem1(id);
  }, [onChangeGem1]);
  const onSelectGem2 = React.useCallback(id => {
    setPicking(null);
    onChangeGem2(id);
  }, [onChangeGem2]);

  const onClosePicker = React.useCallback(() => setPicking(null), []);
  const onPickSkill = React.useCallback(e => {
    if (!editing) return;
    const node = e.target.closest('.lap-gem-skill') || e.target;
    setPicking({type: 'skill', position: node.getBoundingClientRect()});
  }, [editing]);
  const onPickGem1 = React.useCallback(e => {
    if (!editing) return;
    if (!data || !data.id1 || (!data.gem1 && numGems >= 11)) return;
    if (builder.category === 'inferno') {
      const gem = findInfernoGem(data.id1, 1);
      if (gem) onChangeGem1(gem);
    } else {
      const node = e.target.closest('.lap-gem-socket') || e.target;
      setPicking({type: 'gem1', position: node.getBoundingClientRect()});
    }
  }, [editing, data, numGems, onChangeGem1, builder.category]);
  const onPickGem2 = React.useCallback(e => {
    if (!editing) return;
    if (!data || !data.id2 || (!data.gem2 && numGems >= 11)) return;
    if (builder.category === 'inferno') {
      const gem = findInfernoGem(data.id2, 2);
      if (gem) onChangeGem2(gem);
    } else {
      const node = e.target.closest('.lap-gem-socket') || e.target;
      setPicking({type: 'gem2', position: node.getBoundingClientRect()});
    }
  }, [editing, data, numGems, onChangeGem2, builder.category]);
  const onRemoveGem1 = React.useCallback(e => {
    if (!editing) return;
    e.preventDefault();
    onChangeGem1(null);
  }, [editing, onChangeGem1]);
  const onRemoveGem2 = React.useCallback(e => {
    if (!editing) return;
    e.preventDefault();
    onChangeGem2(null);
  }, [editing, onChangeGem2]);

  let picker = null;
  if (picking) {
    if (picking.type === 'skill') {
      picker = (
        <Popup alignPosition={picking.position} alignMethod={alignMethod} alignPadding={0} onClose={onClosePicker}>
          <BuilderGemSkillPicker charClass={charClass} index={index} onSelect={onSelectSkill}/>
        </Popup>
      );
    } else if (picking.type === 'gem1') {
      picker = (
        <Popup alignPosition={picking.position} alignMethod={alignMethod} alignPadding={0} onClose={onClosePicker}>
          <BuilderGemPicker skill={data.id1} type={1} onSelect={onSelectGem1}/>
        </Popup>
      );
    } else if (picking.type === 'gem2') {
      picker = (
        <Popup alignPosition={picking.position} alignMethod={alignMethod} alignPadding={0} onClose={onClosePicker}>
          <BuilderGemPicker skill={data.id2} type={2} onSelect={onSelectGem2}/>
        </Popup>
      );
    }
  }

  const skill = data && (Data.skillGroup[data.id1 || data.id2] || Data.skills[data.id1 || data.id2]);
  if (!skill) {
    if (!editing) return null;
    return <>
      <li className="lap-GemSlot">
        <div className="lap-skill-entry lap-empty" onClick={onPickSkill}>
          <div className="lap-skill-icon lap-add"/>
          <div className="lap-skill-name">Select skill...</div>
        </div>
      </li>
      {picker}
    </>;
  }

  const gem1 = Data.items[data.gem1], gem2 = Data.items[data.gem2];

  return <>
    <li className="lap-GemSlot">
      <GemSkillDisplay ids={data} onClick={onPickSkill}/>
      {gem1 ? (
        <WithItemTooltip item={{id: data.gem1, skill: data.id1}}>
          <div className={classNames("lap-gem-socket", itemBackground(gem1))} onClick={onPickGem1} onContextMenu={onRemoveGem1}>
            <ItemIcon item={gem1}/>
          </div>
        </WithItemTooltip>
      ) : (
        <Tooltip disabled={numGems < 11 || !editing} tooltip="No available gem slots">
          <div className={classNames("lap-gem-socket lap-empty", {"lap-hidden": !data.id1})} onClick={onPickGem1}/>
        </Tooltip>
      )}
      {gem2 ? (
        <WithItemTooltip item={{id: data.gem2, skill: data.id2}}>
          <div className={classNames("lap-gem-socket", itemBackground(gem2))} onClick={onPickGem2} onContextMenu={onRemoveGem2}>
            <ItemIcon item={gem2}/>
          </div>
        </WithItemTooltip>
      ) : (
        <Tooltip disabled={numGems < 11 || !editing} tooltip="No available gem slots">
          <div className={classNames("lap-gem-socket lap-empty", {"lap-hidden": !data.id2})} onClick={onPickGem2}/>
        </Tooltip>
      )}
      {!!editing && (
        <div className="lap-gem-remove" onClick={() => onSelectSkill(null)}/>
      )}
    </li>
    {picker}
  </>;
}

export default function BuilderGems() {
  const editing = EditingContext.useStateView("builderEditing");

  const builder = Store.useBuilder();

  const sorted = React.useMemo(() => {
    if (!builder.gems) return [];
    return sortSkillsById(builder.gems.map((gem, index) => ({...gem, index})));
  }, [builder.gems]);
  const usedGems = countGems(builder);

  const slots = sorted.map(({index}) => <BuilderGem key={index} index={index} editing={editing} charClass={builder.class}/>);
  if (editing && usedGems < 11 && sorted.length < 11) slots.push(<BuilderGem key={-1} editing={true} charClass={builder.class}/>);
  const midPoint = slots.length >= 11 ? 6 : 5;

  const onClear = React.useCallback(builder => ({...builder, gems: []}), []);

  return (
    <div className="lap-BuilderBlock lap-BuilderGems">
      <div className="lap-BuilderBlockHeader">
        Gems
        {!!(editing && builderHasGems(builder)) && <BlockClearButton onClear={onClear}/>}
      </div>
      <div className="lap-gems">
        <ul className="lap-column">{slots.slice(0, midPoint)}</ul>
        <ul className="lap-column">{slots.slice(midPoint)}</ul>
      </div>
    </div>
  );
}
